import React, { useMemo, useState } from "react";
import { Table, Typography, Dropdown, Input } from "antd";
import type { MenuProps } from "antd";
import dayjs from "dayjs";
import {
  PanelUser,
  useListAllUsersQuery,
  useUpdateUserPermissionsMutation,
} from "../../actions/superuser";
import { UserOrganization } from "../../types";
import ManagePermissionsModal from "./ManagePermissionsModal";

const { Text } = Typography;

/**
 * Users in one organization, or the unaffiliated bucket.
 *
 * Bucketed by SCOUT MEMBERSHIP, not by the user's primary organization. The
 * primary organization is IDX's field, and someone can hold Scout access to an
 * organization that is not their primary one - listing them by primary would
 * hide them from the very organization they can actually see in Scout.
 *
 * A consequence worth knowing: a user with memberships in several organizations
 * appears under each of them. That is correct here - they really do belong to
 * all of them - so the buckets are not a partition.
 *
 * `org` of undefined means Unaffiliated: no Scout membership for any
 * organization Scout has. That covers both a user who was never granted one and
 * a user whose organization has since been deleted, which are equally stranded
 * and need the same fix. It can only be answered against the organization list.
 */
const UsersTable: React.FC<{
  org?: string;
  organizations: UserOrganization[];
}> = ({ org, organizations }) => {
  const { data, isFetching } = useListAllUsersQuery();
  const [updatePermissions, { isLoading: saving }] =
    useUpdateUserPermissionsMutation();
  const [managing, setManaging] = useState<PanelUser | null>(null);
  const [search, setSearch] = useState("");

  const known = useMemo(
    () => new Set(organizations.map((o) => o.uuid)),
    [organizations],
  );

  const users = useMemo(() => {
    const all = data?.users || [];
    const term = search.trim().toLowerCase();
    return all
      .filter((u) => {
        // memberships arrive already filtered to Scout by the backend.
        const scoutOrgs = (u.memberships || []).map((m) => m.org);
        return org
          ? scoutOrgs.includes(org)
          : !scoutOrgs.some((o) => known.has(o));
      })
      .filter(
        (u) =>
          !term ||
          (u.email || "").toLowerCase().includes(term) ||
          (u.name || "").toLowerCase().includes(term),
      );
  }, [data, org, known, search]);

  const menuFor = (user: PanelUser): MenuProps => ({
    items: [
      {
        key: "manage",
        label: "Manage Permissions",
        onClick: () => setManaging(user),
      },
    ],
  });

  return (
    <>
      <Input.Search
        allowClear
        placeholder="Search name or email"
        className="mb-4 max-w-sm"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {data?.truncated && (
        <Text type="warning" className="block mb-2">
          Showing the first 1000 users only; the directory is larger.
        </Text>
      )}

      <Table
        rowKey="user_id"
        size="small"
        loading={isFetching}
        dataSource={users}
        // Right-click a row for Manage Permissions, matching IDX. The Dropdown
        // below wraps each row and already suppresses the browser menu, so there
        // is deliberately no onRow handler competing with it.
        components={{
          body: {
            row: (props: any) => {
              const user = users.find(
                (u) => u.user_id === props["data-row-key"],
              );
              return user ? (
                <Dropdown menu={menuFor(user)} trigger={["contextMenu"]}>
                  <tr {...props} />
                </Dropdown>
              ) : (
                <tr {...props} />
              );
            },
          },
        }}
        columns={[
          {
            title: "Name",
            dataIndex: "name",
            sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
          },
          {
            title: "Email",
            dataIndex: "email",
            sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
          },
          {
            title: "Created",
            dataIndex: "created_at",
            sorter: (a, b) =>
              new Date(a.created_at || 0).getTime() -
              new Date(b.created_at || 0).getTime(),
            render: (v?: string) => (v ? dayjs(v).format("YYYY-MM-DD") : "—"),
          },
        ]}
      />

      <ManagePermissionsModal
        user={managing}
        organizations={organizations}
        onClose={() => setManaging(null)}
        saving={saving}
        onSave={(user, orgs) =>
          updatePermissions({
            userId: user.user_id,
            organizations: orgs,
          }).unwrap()
        }
      />
    </>
  );
};

export default UsersTable;
